'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

/**
 * 基础模态框组件
 * 使用 React Portal 确保模态框始终显示在页面中央，不受父组件位置限制
 *
 * @param {boolean} isOpen - 是否显示模态框
 * @param {Function} onClose - 关闭模态框的回调函数
 * @param {ReactNode} children - 模态框内容
 * @param {string} className - 额外的样式类
 * @returns {JSX.Element | null} - 返回模态框组件或 null
 */
const BaseModal = ({ isOpen, onClose, children, className = '' }) => {
  // 创建一个状态来跟踪是否可以在客户端渲染 Portal
  const [isMounted, setIsMounted] = useState(false);

  // 在客户端渲染完成后设置 mounted 状态
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 处理ESC键关闭
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // 当模态框打开时禁止背景滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 如果不是在客户端或模态框未打开，不渲染任何内容
  if (!isMounted || !isOpen) return null;

  // 创建要渲染到 Portal 的模态框内容
  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 md:p-20">
      {/* Backdrop - 添加模糊和渐变效果 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-gradient-to-b from-black/80 to-black/60 backdrop-blur-md"
      />

      {/* Modal - 优化动画和样式 */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{
          type: 'spring',
          duration: 0.5,
          bounce: 0.2,
        }}
        onClick={(e) => e.stopPropagation()}
        className={`scrollbar-thin scrollbar-track-gray-800/50 scrollbar-thumb-[#8735D9]/50 hover:scrollbar-thumb-[#8735D9]/70 relative max-h-[90vh] overflow-y-auto rounded-2xl border border-[#333333]/30 bg-gradient-to-b from-black/90 to-black/95 shadow-[0_0_50px_rgba(194,255,11,0.1)] backdrop-blur-xl ${className} `}
      >
        {/* 关闭按钮 - 优化样式 */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full border border-gray-800 bg-black/50 p-2 text-gray-400 transition-all duration-300 hover:border-[#8735D9]/50 hover:bg-[#8735D9]/10 hover:text-[#8735D9]"
          aria-label="关闭模态框"
          title="关闭模态框"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {children}
      </motion.div>
    </div>
  );

  // 使用 createPortal 将模态框内容渲染到 body 下
  return createPortal(<AnimatePresence>{isOpen && modalContent}</AnimatePresence>, document.body);
};

export default BaseModal;
